/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { AuthProvider, useAuth } from '@/context/AuthContext.js'
import { ClientProvider, useClient } from '@/context/ClientContext.js'
import { ConfigProvider } from '@/context/ConfigContext.js'
import { PermissionsProvider } from '@/context/PermissionsContext.js'
import { RumProvider } from '@/context/RumContext.js'
import { refusalKindOf } from '@/context/SupportSessionContext.js'
import { ThemeProvider } from '@/context/ThemeContext.js'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import React from 'react'

import { Spinner } from '@droidmap/frontend-ui'

import { routeTree } from './routeTree.gen'

// Create a new router instance
// A support session's refusal is the same answer every time: retrying it
// only repeats the request and delays the page saying why (droidmap#2926).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        refusalKindOf(error) === undefined && failureCount < 3,
    },
  },
})
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    auth: undefined!,
    client: undefined!,
    queryClient: queryClient,
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Component that waits for auth before rendering ClientProvider and AppRouter
function ProtectedApp() {
  const auth = useAuth()

  if (auth.isLoading) {
    // Render a full-page loading indicator while auth is initializing
    return <Spinner size="page" className="text-accent" />
  }

  // Auth context is loaded, proceed to render dependent providers and the router
  return (
    <ClientProvider>
      <RumProvider>
        <PermissionsProvider>
          <AppRouter />
        </PermissionsProvider>
      </RumProvider>
    </ClientProvider>
  )
}

function AppRouter() {
  const auth = useAuth()
  const client = useClient()

  const routerContext = React.useMemo(
    () => ({
      auth,
      client,
      queryClient,
    }),
    [auth, client],
  )

  return <RouterProvider router={router} context={routerContext} />
}

// Main App component orchestrates the providers
export function App() {
  return (
    <div className="text-text h-full">
      <ConfigProvider>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <ProtectedApp />
            </AuthProvider>
          </QueryClientProvider>
        </ThemeProvider>
      </ConfigProvider>
    </div>
  )
}

export default App
